/**
 * FileCard Component
 * Displays a file or folder in card format with preview, selection, actions, and favourites
 */

import React, { useState } from 'react';
import ObsoleteBadge from './ObsoleteBadge';

// File type to icon mapping
const getFileIcon = (extension, isFolder) => {
  if (isFolder) return { icon: 'fa-folder', className: 'folder' };

  const ext = (extension || '').toLowerCase();
  const iconMap = {
    // Documents
    pdf: { icon: 'fa-file-pdf', className: 'pdf' },
    doc: { icon: 'fa-file-word', className: 'doc' },
    docx: { icon: 'fa-file-word', className: 'docx' },
    xls: { icon: 'fa-file-excel', className: 'xls' },
    xlsx: { icon: 'fa-file-excel', className: 'xlsx' },
    ppt: { icon: 'fa-file-powerpoint', className: 'ppt' },
    pptx: { icon: 'fa-file-powerpoint', className: 'pptx' },
    txt: { icon: 'fa-file-alt', className: 'txt' },
    csv: { icon: 'fa-file-csv', className: 'csv' },
    // Images
    jpg: { icon: 'fa-file-image', className: 'image' },
    jpeg: { icon: 'fa-file-image', className: 'image' },
    png: { icon: 'fa-file-image', className: 'image' },
    gif: { icon: 'fa-file-image', className: 'image' },
    svg: { icon: 'fa-file-image', className: 'image' },
    webp: { icon: 'fa-file-image', className: 'image' },
    // Video
    mp4: { icon: 'fa-file-video', className: 'video' },
    mov: { icon: 'fa-file-video', className: 'video' },
    avi: { icon: 'fa-file-video', className: 'video' },
    webm: { icon: 'fa-file-video', className: 'video' },
    // Audio
    mp3: { icon: 'fa-file-audio', className: 'audio' },
    wav: { icon: 'fa-file-audio', className: 'audio' },
    // Archives
    zip: { icon: 'fa-file-archive', className: 'archive' },
    rar: { icon: 'fa-file-archive', className: 'archive' },
    '7z': { icon: 'fa-file-archive', className: 'archive' },
    // Code
    js: { icon: 'fa-file-code', className: 'code' },
    html: { icon: 'fa-file-code', className: 'code' },
    css: { icon: 'fa-file-code', className: 'code' },
    json: { icon: 'fa-file-code', className: 'code' },
  };

  return iconMap[ext] || { icon: 'fa-file', className: 'default' };
};

// Check if file is an image that can be previewed
const isPreviewable = (extension) => {
  const previewable = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  return previewable.includes((extension || '').toLowerCase());
};

// Generate thumbnail URL with HubSpot resize parameter for efficiency
// HubSpot supports: thumb, icon, medium, preview
const getThumbnailUrl = (url) => {
  if (!url) return null;
  
  // Check if URL is a HubSpot URL that supports resizing
  if (url.includes('hubspot') || url.includes('hs-sandbox')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}size=thumb`;
  }
  
  // For other URLs (S3, etc.), return as-is
  return url;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const FileCard = ({
  file,
  isFolder = false,
  onClick,
  onDownload,
  isSelected = false,
  onSelect,
  selectionMode = false,
  isFavourite = false,
  onToggleFavourite,
  onDownloadCurrent
}) => {
  const [imageError, setImageError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [togglingFavourite, setTogglingFavourite] = useState(false);

  const { icon, className } = getFileIcon(file.extension, isFolder);
  const canPreview = !isFolder && isPreviewable(file.extension) && !imageError;
  const previewUrl = file.friendly_url || file.s3_url;
  const thumbnailUrl = getThumbnailUrl(previewUrl);

  const handleClick = (e) => {
    e.preventDefault();
    if (selectionMode && !isFolder) {
      onSelect(file.id);
    } else if (isFolder) {
      onClick(file.id, file.name);
    }
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect(file.id);
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFolder || downloading) return;

    setDownloading(true);
    try {
      await onDownload(file.id, file.name, file.extension);
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleToggleFavourite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (togglingFavourite || !onToggleFavourite) return;

    setTogglingFavourite(true);
    try {
      await onToggleFavourite(file.id, isFolder ? 'folder' : 'file');
    } finally {
      setTogglingFavourite(false);
    }
  };

  return (
    <div
      className={`rf-file-card ${isSelected ? 'selected' : ''} ${isFavourite ? 'favourited' : ''} ${file.obsolete ? 'obsolete' : ''}`}
      onClick={handleClick}
    >
      {/* Favourite button */}
      {onToggleFavourite && (
        <button
          className={`rf-file-card-favourite ${isFavourite ? 'active' : ''}`}
          onClick={handleToggleFavourite}
          disabled={togglingFavourite}
          title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
        >
          <i className={`${isFavourite ? 'fas' : 'far'} fa-star`}></i>
        </button>
      )}

      {/* Selection checkbox for files */}
      {!isFolder && (
        <div className="rf-file-card-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${file.name}`}
          />
        </div>
      )}

      <div className="rf-file-card-preview">
        {canPreview && thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={file.name}
            width="180"
            height="100"
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <i className={`rf-file-card-icon fas ${icon} ${className}`}></i>
        )}
      </div>

      <div className="rf-file-card-body">
        <h4 className="rf-file-card-name" title={file.name}>
          {file.name}
        </h4>
        <div className="rf-file-card-meta">
          {!isFolder && file.extension && (
            <span className="rf-file-card-extension">{file.extension}</span>
          )}
          {file.size && <span>{formatFileSize(file.size)}</span>}
          {isFolder && <span>Folder</span>}
        </div>
        {!isFolder && <ObsoleteBadge obsolete={file.obsolete} onDownloadCurrent={onDownloadCurrent} />}
      </div>

      <div className="rf-file-card-actions">
        {isFolder ? (
          <button className="rf-file-card-action" onClick={handleClick}>
            <i className="fas fa-folder-open"></i>
            Open
          </button>
        ) : (
          <>
            {previewUrl && (
              <button className="rf-file-card-action" onClick={handlePreview}>
                <i className="fas fa-eye"></i>
                View
              </button>
            )}
            <button
              className="rf-file-card-action download"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Downloading...
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i>
                  Download
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FileCard;
